import {
  concat,
  filter,
  find,
  flow,
  get,
  gte,
  includes,
  isEmpty,
  isNil,
  lt,
  map,
  merge,
  set,
  some,
  unset,
  has,
} from 'lodash';

import { IQohInventoryItemVenue } from '@app/shared/types';
import { TRANSFER_STATUS_VALUE } from '@app/shared/types/enums/transfer-status.enum';
import { calcTotalCost } from '@app/shared/utils/transfer-calc-utils';
import { ENTITY_ID_PATH } from '@config/global-constants';

import { TRANSFER_FORM_FIELD_PATHS } from '../../transfer/transfer-form-field-paths';
import { DEFAULT_REPLENISH_PERCENT } from '../replenish.constants';

import {
  IReplenishDestination,
  ReplenishDestItemParams,
  ReplenishSingleDestParams,
} from '../replenish.types';
import {
  getHasInitialCountItems,
  getQohAndPendingQuantity,
} from '../replenish.utils';
import { AbstractReplenishStrategy } from './abstract-replenish-strategy';
import { ReplenishDataSource } from '../replenish-data';
import { replenishInventoryItem } from './utils/replenish-calc.utils';
import { qohCalculator } from './calculators';

class ReplenishTransferQOHStrategy<
  T extends IReplenishDestination = IReplenishDestination,
> extends AbstractReplenishStrategy<T> {
  public appendUnsavedWithOriginal(
    params: ReplenishSingleDestParams,
  ): IReplenishDestination {
    const { destination, additionalData } = params;

    const unSavedData = get(additionalData, 'unSavedData');

    // not have unsaved data, just return
    if (isNil(unSavedData)) {
      return destination;
    }

    const replenishedInventoryItems = destination.inventory_items;

    const replenishedItemIds = map(replenishedInventoryItems, ENTITY_ID_PATH);

    const additionalUnsavedItems = filter(
      get(unSavedData, destination.toSite)?.items,
      (item) => !includes(replenishedItemIds, item.entity_id),
    );

    const originInventoryItems = get(
      params.additionalData,
      'remoteInventoryItems',
      [],
    );

    // use form site item instead those not configure threshold but user edited items,
    // so we can use completed items to calc grand total and transfer status
    const additionalChangedItems: IQohInventoryItemVenue[] = map(
      additionalUnsavedItems,
      (unSavedItem) => {
        const itemWithAllProperty =
          find(originInventoryItems, {
            entity_id: unSavedItem.entity_id,
          }) ?? {};
        unset(itemWithAllProperty, 'thresholds');

        return {
          ...itemWithAllProperty,
          ...unSavedItem,
        };
      },
    );

    const completedInventoryItems = concat(
      replenishedInventoryItems,
      additionalChangedItems,
    );

    set(
      destination,
      TRANSFER_FORM_FIELD_PATHS.inventoryItemVenues,
      completedInventoryItems,
    );

    return destination;
  }

  /**
   * should call first
   * append unsaved changes to destination(totals, status, isChanged...)
   *
   * @param params replenish params
   * @returns replenish params with updated destination
   */
  public appendUnsavedChanges(
    params: ReplenishSingleDestParams,
  ): ReplenishSingleDestParams {
    const { destination, additionalData } = params;

    const unSavedData = get(additionalData, 'unSavedData');

    // not have unsaved data, just return
    if (isNil(unSavedData)) {
      return params;
    }

    const mergedDestinations = this.appendUnsavedWithOriginal(params);

    const replenishedInventoryItems = mergedDestinations.inventory_items;

    const hasInitialCountItems = getHasInitialCountItems(
      replenishedInventoryItems,
    );

    destination.reallyChangedItems = map(hasInitialCountItems, (item) => {
      return {
        _id: get(item, '_id'),
        item_entity_id: get(item, ENTITY_ID_PATH),
        [TRANSFER_FORM_FIELD_PATHS.initialCount]: get(
          item,
          TRANSFER_FORM_FIELD_PATHS.initialCount,
        ),
        [ENTITY_ID_PATH]: get(item, ENTITY_ID_PATH),
        [TRANSFER_FORM_FIELD_PATHS.measureEntityId]: get(
          item,
          TRANSFER_FORM_FIELD_PATHS.measureEntityId,
        ),
      };
    });

    return {
      ...params,
      destination,
    };
  }

  /**
   * should after appendUnsavedChanges
   * append summary info to destination(totals, status, isChanged...)
   *
   * @param params replenish params
   * @returns replenish params with updated destination
   */
  public appendSummaryInfo(
    params: ReplenishSingleDestParams,
  ): ReplenishSingleDestParams {
    const { destination, additionalData } = params;

    const replenishedInventoryItems = destination.inventory_items;

    const hasInitialCountItems = getHasInitialCountItems(
      replenishedInventoryItems,
    );

    destination.totalCost = calcTotalCost(replenishedInventoryItems).valueOf();
    destination.request_cost = gte(destination.totalCost, 0)
      ? destination.totalCost
      : 0;
    destination.reversed_cost = lt(destination.totalCost, 0)
      ? destination.totalCost
      : 0;

    destination.isChanged = !isEmpty(hasInitialCountItems);

    destination.status = some(hasInitialCountItems, (item) => {
      return (
        item[TRANSFER_FORM_FIELD_PATHS.initialCount] > 0 ||
        item[TRANSFER_FORM_FIELD_PATHS.initialCount] < 0
      );
    })
      ? TRANSFER_STATUS_VALUE.New
      : TRANSFER_STATUS_VALUE.Null;

    const unSavedData = get(additionalData, 'unSavedData');

    const unsavedTransfer = get(unSavedData, destination.toSite);

    if (!isNil(unsavedTransfer)) {
      // update grand totals in the unsaved transfer to keep grand total in same when refresh
      merge(unsavedTransfer, {
        grand_total: {
          // should calc again after re calc total cost
          request_cost: destination.request_cost,
          reversed_cost: destination.received_cost,
          received_cost: unsavedTransfer?.grand_total?.received_cost,
        },
      });
    }

    return {
      ...params,
      destination,
    };
  }

  /**
   * replenish destination single item method, you can override this to use different calc method here
   *
   * @param params calc params
   * @returns calc result
   */
  public replenishDestinationItem(
    params: ReplenishDestItemParams,
  ): IQohInventoryItemVenue {
    return replenishInventoryItem({
      item: params.item,
      fieldPath: TRANSFER_FORM_FIELD_PATHS,
      threshold: params.threshold,
      replenishType: params.replenishType,
      percent: params.percent,
      calculator: qohCalculator,
      additionalData: params.additionalData,
    });
  }

  public replenishDestination(
    params: ReplenishSingleDestParams,
  ): IReplenishDestination {
    const { destination, additionalData } = params;

    const objectFields = TRANSFER_FORM_FIELD_PATHS;

    const replenishPercent = get(
      destination,
      'percent',
      DEFAULT_REPLENISH_PERCENT,
    );

    const inventoryItems = map(destination.inventory_items, (item) => {
      const unSavedData = params.additionalData?.unSavedData;

      let mergedItem = {
        ...item,
      };

      if (!isNil(get(unSavedData, destination.toSite))) {
        const targetInventoryItem = find(
          unSavedData[destination.toSite].items,
          { entity_id: item.entity_id },
        );

        mergedItem = { ...mergedItem, ...targetInventoryItem };
      }

      if (
        !has(mergedItem, 'qohFrom') &&
        (has(additionalData, ReplenishDataSource.Qoh) ||
          has(additionalData, ReplenishDataSource.PendingQty))
      ) {
        set(
          mergedItem,
          'qohFrom',
          getQohAndPendingQuantity(
            item,
            objectFields.siteId,
            additionalData as any,
            destination.fromSite,
          ),
        );
      }

      if (
        !has(mergedItem, 'qohTo') &&
        (has(additionalData, ReplenishDataSource.Qoh) ||
          has(additionalData, ReplenishDataSource.PendingQty))
      ) {
        set(
          mergedItem,
          'qohTo',
          getQohAndPendingQuantity(
            item,
            objectFields.siteIdTo,
            additionalData as any,
            destination.toSite,
          ),
        );
      }

      return mergedItem;
    });

    const replenishedInventoryItems = this.replenishDestinationItems({
      additionalData,
      percent: replenishPercent,
      items: inventoryItems,
      replenishApplyItemsScope: params.replenishApplyItemsScope,
      replenishItemsScope: params.replenishItemsScope,
    });

    set(
      destination,
      objectFields.inventoryItemVenues,
      replenishedInventoryItems,
    );
    set(destination, 'replenished', true);

    // append other calc value
    flow(
      this.appendUnsavedChanges.bind(this),
      this.appendSummaryInfo.bind(this),
    )({
      ...params,
      destination,
    });

    return destination;
  }
}

export { ReplenishTransferQOHStrategy };
